import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, Loader2, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';

const ContactSection = ({ t }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [gdprConsent, setGdprConsent] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 5;
    const maxSize = 5 * 1024 * 1024; // 5MB per file
    
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast.error(t.nav.home === 'Acasă' ? 'Fișierul este prea mare (max 5MB)' : 'File too large (max 5MB)');
        return false;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(t.nav.home === 'Acasă' ? 'Doar imagini sunt permise' : 'Only images allowed');
        return false;
      }
      return true;
    });
    
    const newPhotos = validFiles.slice(0, maxFiles - uploadedPhotos.length);
    
    const photoPromises = newPhotos.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            file,
            preview: reader.result,
            name: file.name
          });
        };
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(photoPromises).then(photos => {
      setUploadedPhotos(prev => [...prev, ...photos]);
    });
  };

  const removePhoto = (index) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!gdprConsent) {
      toast.error(t.nav.home === 'Acasă' ? 'Te rugăm să accepți termenii de prelucrare a datelor' : 'Please accept data processing terms');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          photos: uploadedPhotos.map(p => p.preview)
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit inquiry');
      }

      const result = await response.json();
      console.log('Inquiry submitted:', result);
      
      // Google Ads Conversion Tracking
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-18007546219/zD5fCPPB4oscEOuy1YpD',
          'value': 1.0,
          'currency': 'RON'
        });
      }
      
      toast.success(t.contact.success);
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      setUploadedPhotos([]);
      setGdprConsent(false);
    } catch (error) {
      console.error('Contact error:', error);
      toast.error(t.contact.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#0d2e5d] mb-4">
            {t.contact.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Form - 2 columns */}
          <div className="lg:col-span-2">
            <Card className="border-2 shadow-lg">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name & Email */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.contact.form.name} *
                      </label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Ion Popescu"
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.contact.form.email} *
                      </label>
                      <Input
                        required
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="ion@example.com"
                        className="h-12"
                      />
                    </div>
                  </div>

                  {/* Phone & Service */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.contact.form.phone} *
                      </label>
                      <Input
                        required
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="+40 7XX XXX XXX"
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.contact.form.service} *
                      </label>
                      <Select
                        required
                        value={formData.service}
                        onValueChange={(value) => handleChange('service', value)}
                      >
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder={t.contact.form.selectService} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="couch">
                            {t.contact.form.couchCleaning}
                          </SelectItem>
                          <SelectItem value="mattress">
                            {t.contact.form.mattressCleaning}
                          </SelectItem>
                          <SelectItem value="both">
                            {t.contact.form.both}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.contact.form.message}
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder={t.contact.form.messagePlaceholder}
                      rows={5}
                      className="resize-none"
                    />
                  </div>

                  {/* Photo Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <ImageIcon className="w-4 h-4 inline mr-1" />
                      {t.nav.home === 'Acasă' ? 'Fotografii (opțional)' : 'Photos (optional)'}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      {t.nav.home === 'Acasă' 
                        ? 'Adaugă fotografii cu canapelele/saltelele (max 5 fotografii, 5MB fiecare)'
                        : 'Add photos of couches/mattresses (max 5 photos, 5MB each)'}
                    </p>
                    
                    <div className="space-y-3">
                      {uploadedPhotos.length < 5 && (
                        <label className="cursor-pointer">
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-[#2196f3] transition-colors">
                            <div className="flex items-center justify-center gap-2 text-gray-600 hover:text-[#2196f3]">
                              <Upload className="w-5 h-5" />
                              <span className="font-medium">
                                {t.nav.home === 'Acasă' ? 'Adaugă Fotografii' : 'Add Photos'}
                              </span>
                            </div>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handlePhotoUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                      
                      {uploadedPhotos.length > 0 && (
                        <div className="grid grid-cols-3 gap-3">
                          {uploadedPhotos.map((photo, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={photo.preview}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                              />
                              <button
                                type="button"
                                onClick={() => removePhoto(index)}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {uploadedPhotos.length > 0 && (
                        <p className="text-xs text-gray-500">
                          {uploadedPhotos.length} / 5 {t.nav.home === 'Acasă' ? 'fotografii încărcate' : 'photos uploaded'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* GDPR Consent */}
                  <div className="border-t pt-4">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="contact-gdpr-consent"
                        checked={gdprConsent}
                        onChange={(e) => setGdprConsent(e.target.checked)}
                        className="mt-1 w-4 h-4 text-[#2196f3] border-gray-300 rounded focus:ring-[#2196f3]"
                        required
                      />
                      <label htmlFor="contact-gdpr-consent" className="text-sm text-gray-700 leading-relaxed">
                        {t.nav.home === 'Acasă'
                          ? 'Sunt de acord ca datele mele personale să fie prelucrate conform '
                          : 'I agree that my personal data will be processed according to the '}
                        <span className="text-[#2196f3] underline cursor-pointer hover:text-[#1976d2]">
                          {t.nav.home === 'Acasă' ? 'Politicii de confidențialitate GDPR' : 'GDPR Privacy Policy'}
                        </span>
                        . {t.nav.home === 'Acasă' 
                          ? 'Datele tale vor fi folosite doar pentru procesarea solicitării și nu vor fi partajate cu terțe părți.'
                          : 'Your data will only be used for processing your request and will not be shared with third parties.'}
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#2196f3] hover:bg-[#1976d2] text-white h-12 text-lg"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {t.contact.form.submitting}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        {t.contact.form.submit}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info - 1 column */}
          <div className="space-y-6">
            {/* Contact Info Card */}
            <Card className="border-2 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-[#0d2e5d] mb-6">
                  {t.contact.info.title}
                </h3>
                <div className="space-y-6">
                  {/* Phone */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#2196f3]/10 rounded-lg flex items-center justify-center">
                      <Phone className="w-6 h-6 text-[#2196f3]" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        {t.contact.info.phone}
                      </div>
                      <a
                        href={`tel:${companyInfo.phone}`}
                        className="text-[#0d2e5d] font-medium hover:text-[#2196f3] transition-colors"
                      >
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#5cd26f]/10 rounded-lg flex items-center justify-center">
                      <Mail className="w-6 h-6 text-[#5cd26f]" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        {t.contact.info.email}
                      </div>
                      <a
                        href={`mailto:${companyInfo.email}`}
                        className="text-[#0d2e5d] font-medium hover:text-[#2196f3] transition-colors break-all"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#35c6e1]/10 rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#35c6e1]" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        {t.coverage.title}
                      </div>
                      <div className="text-[#0d2e5d] font-medium">
                        {companyInfo.coverageArea}
                      </div>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#2196f3]/10 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-[#2196f3]" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        {t.contact.info.hours}
                      </div>
                      <div className="text-[#0d2e5d] font-medium">
                        {t.contact.info.hoursValue}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Map Card */}
            <Card className="border-2 shadow-lg overflow-hidden">
              <div className="h-64 bg-gray-200">
                <iframe
                  src="https://maps.google.com/maps?q=Strada+Lunga+100,+Brasov,+Romania&t=&z=16&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Location Map"
                ></iframe>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
